import React, { useState, useMemo } from 'react';
import { TrendingUp, RotateCcw, Award, AlertTriangle, Percent, X, Download } from 'lucide-react';

/**
 * Compound Interest Plan — a flip card inside the Session Manager.
 * Shows the trader what happens if they compound their full payout after every win,
 * how many consecutive wins they need to reach a goal, and how many consecutive
 * losses would blow the account.
 */
const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CompoundPlanCard({ open, onClose, defaultBalance = 50, defaultEntry = 5, defaultPayout = 92, traderName = '' }) {
  const [startBalance, setStartBalance] = useState(defaultBalance);
  const [firstEntry, setFirstEntry] = useState(defaultEntry);
  const [wins, setWins] = useState(10);
  const [losses, setLosses] = useState(3);
  const [payout, setPayout] = useState(defaultPayout);
  const [downloading, setDownloading] = useState(false);

  const downloadPDF = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(`${API}/compound-plan/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_balance: Number(startBalance) || 0,
          first_entry: Number(firstEntry) || 0.01,
          wins: Number(wins) || 1,
          losses: Number(losses) || 0,
          payout: Number(payout) || 92,
          trader_name: traderName || undefined,
          asset: `OTC ${payout}%`,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `forthewisepro_compound_plan_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Could not generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const plan = useMemo(() => {
    const p = Math.max(0, Number(payout) || 0) / 100;
    const start = Math.max(0, Number(startBalance) || 0);
    const first = Math.max(0.01, Number(firstEntry) || 0.01);
    const W = Math.max(0, Math.floor(Number(wins) || 0));
    const L = Math.max(0, Math.floor(Number(losses) || 0));

    // Compound win ladder
    const winLadder = [];
    let stake = first;
    let balance = start;
    let cumulativeProfit = 0;
    for (let i = 1; i <= W; i++) {
      const profit = stake * p;
      const newBalance = balance + profit;
      cumulativeProfit += profit;
      winLadder.push({
        trade: i,
        stake: Number(stake.toFixed(2)),
        profit: Number(profit.toFixed(2)),
        balance: Number(newBalance.toFixed(2)),
        cumulativeProfit: Number(cumulativeProfit.toFixed(2)),
        roi: Number(((cumulativeProfit / start) * 100).toFixed(1)),
      });
      // Next stake = current stake + its full payout (compounding the profit)
      stake = stake + profit;
      balance = newBalance;
    }

    // Loss ladder — same-stake losses starting from firstEntry (fixed-risk mode)
    const lossLadderFixed = [];
    let bal = start;
    for (let i = 1; i <= L; i++) {
      bal -= first;
      lossLadderFixed.push({
        trade: i,
        stake: Number(first.toFixed(2)),
        balanceAfter: Number(bal.toFixed(2)),
        blown: bal <= 0,
      });
    }
    // How many fixed-stake losses blow the account?
    const lossesToBlow = first > 0 ? Math.ceil(start / first) : Infinity;

    // Loss ladder — martingale-style (double after each loss) — useful for warning
    const lossLadderCompound = [];
    let bal2 = start;
    let stake2 = first;
    let count = 0;
    while (bal2 > 0 && count < 15) {
      count += 1;
      bal2 -= stake2;
      lossLadderCompound.push({
        trade: count,
        stake: Number(stake2.toFixed(2)),
        balanceAfter: Number(bal2.toFixed(2)),
        blown: bal2 <= 0,
      });
      stake2 = stake2 * 2;
      if (bal2 <= 0) break;
    }

    const finalBalance = winLadder.length ? winLadder[winLadder.length - 1].balance : start;
    const totalProfit = winLadder.length ? winLadder[winLadder.length - 1].cumulativeProfit : 0;
    const roi = start > 0 ? Number(((totalProfit / start) * 100).toFixed(1)) : 0;
    const finalStake = winLadder.length ? winLadder[winLadder.length - 1].stake + winLadder[winLadder.length - 1].profit : first;

    return {
      winLadder, lossLadderFixed, lossLadderCompound,
      finalBalance, totalProfit, roi, finalStake,
      lossesToBlow,
    };
  }, [startBalance, firstEntry, wins, losses, payout]);

  if (!open) return null;

  return (
    <div className="bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-red-500/10 border border-amber-500/30 rounded-xl p-3 space-y-3 animate-in fade-in duration-300" data-testid="compound-plan-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
            <TrendingUp className="w-3.5 h-3.5 text-amber-300" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-amber-300">Compound Goal Plan</h4>
            <p className="text-[10px] text-gray-400">Reinvest full payout on every win</p>
          </div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="compound-close">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-gray-400 mb-1 block">Start Balance ($)</label>
          <input type="number" value={startBalance || ''} onChange={e => setStartBalance(parseFloat(e.target.value) || 0)} onFocus={e => e.target.select()} placeholder="50" className="w-full bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-xs text-white" data-testid="compound-start-balance" />
        </div>
        <div>
          <label className="text-[10px] text-gray-400 mb-1 block">First Entry ($)</label>
          <input type="number" value={firstEntry || ''} onChange={e => setFirstEntry(parseFloat(e.target.value) || 0)} onFocus={e => e.target.select()} placeholder="5" className="w-full bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-xs text-white" data-testid="compound-first-entry" />
        </div>
        <div>
          <label className="text-[10px] text-gray-400 mb-1 block">Wins in a Row</label>
          <input type="number" value={wins || ''} onChange={e => setWins(parseInt(e.target.value) || 0)} onFocus={e => e.target.select()} placeholder="10" min="1" max="20" className="w-full bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-xs text-white" data-testid="compound-wins" />
        </div>
        <div>
          <label className="text-[10px] text-gray-400 mb-1 block">Losses in a Row</label>
          <input type="number" value={losses || ''} onChange={e => setLosses(parseInt(e.target.value) || 0)} onFocus={e => e.target.select()} placeholder="3" min="0" max="15" className="w-full bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-xs text-white" data-testid="compound-losses" />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] text-gray-400 mb-1 block flex items-center gap-1"><Percent className="w-2.5 h-2.5" /> Payout %</label>
          <select value={payout} onChange={e => setPayout(parseInt(e.target.value))} className="w-full bg-slate-800 border border-white/10 rounded px-2 py-1.5 text-xs text-white" data-testid="compound-payout">
            {[70, 75, 78, 80, 82, 85, 87, 88, 90, 91, 92, 93, 94, 95].map(p => <option key={p} value={p}>{p}%</option>)}
          </select>
        </div>
      </div>

      {/* Summary badge */}
      <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-2 grid grid-cols-3 gap-1 text-center">
        <div>
          <p className="text-[9px] uppercase text-gray-400 tracking-wider">If {wins}W</p>
          <p className="text-sm font-bold text-emerald-400" data-testid="compound-final-balance">${plan.finalBalance.toFixed(2)}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase text-gray-400 tracking-wider">Profit</p>
          <p className="text-sm font-bold text-emerald-400" data-testid="compound-total-profit">+${plan.totalProfit.toFixed(2)}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase text-gray-400 tracking-wider">ROI</p>
          <p className="text-sm font-bold text-emerald-400" data-testid="compound-roi">{plan.roi}%</p>
        </div>
      </div>

      {/* Download PDF */}
      <button
        onClick={downloadPDF}
        disabled={downloading}
        className="w-full py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:opacity-60 disabled:cursor-wait text-black font-bold text-xs flex items-center justify-center gap-2 transition-all"
        data-testid="compound-download-pdf"
      >
        <Download className="w-3.5 h-3.5" />
        {downloading ? 'Generating PDF…' : 'Download Printable Plan (PDF)'}
      </button>

      {/* Win ladder */}
      <div className="rounded-lg bg-slate-900/60 border border-white/10 overflow-hidden">
        <div className="px-2 py-1.5 bg-emerald-500/10 border-b border-white/10 flex items-center gap-1.5">
          <Award className="w-3 h-3 text-emerald-400" />
          <span className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider">Win ladder (compounding payout)</span>
        </div>
        <div className="max-h-48 overflow-y-auto">
          <table className="w-full text-[10px]" data-testid="compound-win-ladder">
            <thead className="bg-slate-800/50 sticky top-0">
              <tr className="text-gray-400">
                <th className="px-1.5 py-1 text-left">#</th>
                <th className="px-1.5 py-1 text-right">Stake</th>
                <th className="px-1.5 py-1 text-right">Payout</th>
                <th className="px-1.5 py-1 text-right">Balance</th>
                <th className="px-1.5 py-1 text-right">ROI</th>
              </tr>
            </thead>
            <tbody>
              {plan.winLadder.map(row => (
                <tr key={row.trade} className="border-t border-white/5 hover:bg-emerald-500/5">
                  <td className="px-1.5 py-1 text-gray-500 font-mono">#{row.trade}</td>
                  <td className="px-1.5 py-1 text-right text-white font-mono">${row.stake.toFixed(2)}</td>
                  <td className="px-1.5 py-1 text-right text-emerald-400 font-mono">+${row.profit.toFixed(2)}</td>
                  <td className="px-1.5 py-1 text-right text-white font-mono">${row.balance.toFixed(2)}</td>
                  <td className="px-1.5 py-1 text-right text-emerald-400 font-mono">{row.roi}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Loss warning */}
      <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 overflow-hidden">
        <div className="px-2 py-1.5 bg-rose-500/15 border-b border-rose-500/20 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3 text-rose-400" />
            <span className="text-[10px] font-bold text-rose-300 uppercase tracking-wider">Blow-up risk</span>
          </div>
          <span className="text-[10px] text-rose-300 font-mono" data-testid="compound-blow-losses">
            {plan.lossesToBlow === Infinity ? '—' : `${plan.lossesToBlow}L fixed-stake blows account`}
          </span>
        </div>
        <div className="p-2 space-y-1.5">
          <div>
            <p className="text-[9px] text-gray-400 uppercase tracking-wider mb-1">Same-stake losses (${firstEntry}/trade):</p>
            <div className="flex flex-wrap gap-1">
              {plan.lossLadderFixed.map(row => (
                <div key={row.trade} className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${row.blown ? 'bg-rose-600 text-white' : 'bg-slate-800 text-gray-300'}`}>
                  #{row.trade}: ${row.balanceAfter.toFixed(2)}
                </div>
              ))}
            </div>
          </div>
          {plan.lossLadderCompound.length > 0 && (
            <div>
              <p className="text-[9px] text-gray-400 uppercase tracking-wider mb-1">If you Martingale (double after each loss):</p>
              <div className="flex flex-wrap gap-1">
                {plan.lossLadderCompound.map(row => (
                  <div key={row.trade} className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${row.blown ? 'bg-rose-600 text-white' : 'bg-slate-800 text-gray-300'}`}>
                    #{row.trade}: ${row.stake} → ${row.balanceAfter.toFixed(0)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="text-[9px] text-gray-500 text-center italic">
        Educational simulation only — real trading involves risk. Compounding accelerates gains AND losses.
      </p>
    </div>
  );
}
